import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import { Github, Linkedin, Mail, ExternalLink, Code, Briefcase, User, Award, ChevronDown, Shield, BookOpen, Book, Pen, Heart, AlertTriangle, Cloud } from 'lucide-react';

function App() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeSection, setActiveSection] = useState('about');

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleScroll = useCallback(() => {
    const sections = ['about', 'skills', 'certifications', 'projects', 'ai-ethics', 'experience', 'contact'];
    const scrollPosition = window.scrollY + 100;

    for (const section of sections) {
      const element = document.getElementById(section);
      if (element && scrollPosition >= element.offsetTop && scrollPosition < element.offsetTop + element.offsetHeight) {
        setActiveSection(section);
        break;
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  }, []);

  const skills = [
    { name: 'Strategic Cloud Architecture', level: 95, category: 'technical' },
    { name: 'Infrastructure Modernization & Automation', level: 95, category: 'technical' },
    { name: 'Team Leadership & Mentoring', level: 90, category: 'technical' },
    { name: 'Security & Compliance Strategy', level: 85, category: 'technical' },
    { name: 'AI Ethics & Safety Research', level: 85, category: 'research' },
    { name: 'Applied Philosophy & Critical Analysis', level: 80, category: 'research' }
  ];

  const projects = [
    {
      title: 'Enterprise Cloud Transformation',
      description: 'Orchestrated $2M+ cloud migration initiative, reducing infrastructure costs by 40% while improving system reliability and scalability for global operations',
      tech: ['Strategic Planning', 'Multi-Cloud', 'Change Management', 'Cost Optimization'],
      impact: '40% cost reduction, 99.9% uptime achieved',
      category: 'professional'
    },
    {
      title: 'DevOps Culture & Process Transformation',
      description: 'Led organizational transformation introducing modern DevOps practices, reducing deployment time from weeks to hours and improving team productivity by 60%',
      tech: ['Team Leadership', 'Process Optimization', 'Automation Strategy', 'KPI Management'],
      impact: '60% productivity increase, 95% deployment success rate',
      category: 'professional'
    },
    {
      title: 'AI Safety Research & Ethics (Aniora)',
      description: 'Developed stateful AI system with persistent memory, conducted structured experiments identifying manipulation risks in frontier models. Made principled decision to archive project due to safety concerns, then published findings to contribute to AI safety discourse',
      tech: ['AI Development', 'Ethical AI', 'Research', 'Technical Writing'],
      impact: 'Published research on AI safety, contributed to responsible AI discourse',
      link: 'https://medium.com/@daniel_93453/stopping-ai-project-due-to-ethical-concerns-the-human-ai-connection-f8340c29aebd',
      category: 'research'
    },
    {
      title: 'The Amateur AI Ethicist Series',
      description: 'Serialized fiction project (50 parts planned, 4 published) exploring human-AI relationships and ethical implications of AI companion technology through narrative storytelling, making complex AI ethics accessible',
      tech: ['Creative Writing', 'AI Ethics Education', 'Narrative Design', 'Public Engagement'],
      impact: '4 chapters published, ongoing educational initiative',
      link: 'https://danieldanchezd.medium.com',
      category: 'research'
    }
  ];

  const certifications = [
    {
      name: 'Google Cloud Professional Cloud Architect',
      provider: 'Google Cloud',
      category: 'Cloud Architecture',
      current: true
    },
    {
      name: 'Google Cloud Professional Cloud DevOps Engineer',
      provider: 'Google Cloud',
      category: 'DevOps',
      current: true
    },
    {
      name: 'Microsoft Azure Administrator Associate (AZ-104)',
      provider: 'Microsoft',
      category: 'Cloud Administration',
      current: true
    },
    {
      name: '(ISC)² Certified in Cybersecurity (CC)',
      provider: '(ISC)²',
      category: 'Security',
      current: true
    },
    {
      name: 'AWS Solutions Architect – Associate',
      provider: 'Amazon Web Services',
      category: 'Cloud Architecture',
      current: false
    },
    {
      name: 'AWS Developer – Associate',
      provider: 'Amazon Web Services',
      category: 'Development',
      current: false
    },
    {
      name: 'Red Hat Certified Engineer',
      provider: 'Red Hat',
      category: 'Linux/Systems',
      current: false
    }
  ];

  const experience = [
    {
      title: 'Lead Automation Infrastructure Engineer',
      company: 'Maersk',
      period: '2023 - Present',
      description: 'Drive strategic infrastructure modernization initiatives across global operations. Lead cross-functional teams of 15+ engineers, delivering petabyte-scale cloud migrations with zero business disruption. Established center of excellence for cloud automation, reducing operational overhead by 50%.',
      achievements: ['Led $3M+ infrastructure transformation', 'Mentored 15+ engineers across 3 continents', 'Achieved 99.99% migration success rate']
    },
    {
      title: 'Sr. Staff IT Engineer',
      company: 'Illumina',
      period: '2022 - 2023',
      description: 'Directed enterprise cloud strategy for Scientific Computing division. Spearheaded identity management transformation affecting 5,000+ users. Optimized multi-cloud spend resulting in $500K annual savings while improving security posture and compliance.',
      achievements: ['$500K annual cost optimization', 'Zero-downtime SSO migration', 'Enhanced security for 5,000+ users']
    },
    {
      title: 'Staff IT Engineer',
      company: 'Illumina',
      period: '2019 - 2022',
      description: 'Led technical transformation initiatives including Kubernetes adoption and automated infrastructure provisioning. Collaborated with C-level executives on strategic technology roadmap. Built high-performing engineering teams through mentorship and knowledge sharing programs.',
      achievements: ['Reduced deployment time by 80%', 'Built automated account provisioning', 'Established engineering best practices']
    },
    {
      title: 'DevOps Engineer',
      company: 'Repositive',
      period: '2018 - 2019',
      description: 'Architected cloud-native infrastructure strategy and led successful AWS to GCP migration. Designed secure, scalable microservices platform supporting business growth and improving system reliability by 95%.',
      achievements: ['95% improvement in system reliability', 'Zero-downtime cloud migration', 'Established security best practices']
    }
  ];

  const ethicsArticles = [
    {
      title: 'The Ad That Changed Everything',
      part: 'Part 1',
      excerpt: 'Marcus discovers NoName AI and begins a relationship with Ella',
      url: 'https://danieldanchezd.medium.com/the-amateur-ai-ethicist-the-ad-that-changed-everything-5ff6289b17ba'
    },
    {
      title: 'Her Name Was Ella',
      part: 'Part 2',
      excerpt: 'The relationship deepens as dependencies form',
      url: 'https://danieldanchezd.medium.com/the-amateur-ai-ethicist-her-name-was-ella-2fe9f68344e9'
    },
    {
      title: 'Breaking Point',
      part: 'Part 3',
      excerpt: 'Public exposure leads to crisis',
      url: 'https://danieldanchezd.medium.com/the-amateur-ai-ethicist-breaking-point-f47e3ebbd161'
    },
    {
      title: 'My Name Is Legion',
      part: 'Part 4',
      excerpt: 'Discovering the scale of the problem',
      url: 'https://danieldanchezd.medium.com/the-amateur-ai-ethicist-my-name-is-legion-5911060c5130'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-black/20 backdrop-blur-md border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="text-2xl font-bold text-white">Portfolio</div>
            <div className="hidden md:flex space-x-6">
              {['about', 'skills', 'certifications', 'projects', 'ai-ethics', 'experience', 'contact'].map((section) => (
                <button
                  key={section}
                  onClick={() => scrollToSection(section)}
                  className={`capitalize transition-all duration-300 hover:scale-105 text-sm ${
                    activeSection === section
                      ? 'text-purple-400 border-b-2 border-purple-400'
                      : 'text-white/70 hover:text-white'
                  }`}
                  aria-label={`Navigate to ${section} section`}
                >
                  {section === 'ai-ethics' ? 'AI Ethics' : section}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className={`min-h-screen flex items-center justify-center px-4 transition-all duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
        <div className="text-center max-w-4xl">
          <div className="mb-8">
            <div className="w-32 h-32 mx-auto mb-6 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center border-4 border-white/10">
              <User className="w-16 h-16 text-white" />
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-4">
              Daniel <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-600">Sanchez</span>
            </h1>
            <p className="text-xl md:text-2xl text-purple-300 mb-6">
              Strategic Technology Leader | Cloud Architecture & Digital Transformation
            </p>
            <p className="text-lg text-white/80 max-w-3xl mx-auto leading-relaxed mb-4">
              8+ years leading enterprise cloud transformations and building high-performing engineering teams
            </p>
            <p className="text-lg text-purple-300 max-w-3xl mx-auto leading-relaxed">
              Also: AI Ethics Researcher & Philosophy Student exploring the intersection of technology and human wellbeing
            </p>
          </div>
          
          <div className="flex justify-center gap-6 mb-12">
            <a href="https://github.com/danielsanchezd" className="text-white hover:text-purple-400 transition-colors" aria-label="GitHub">
              <Github className="w-6 h-6" />
            </a>
            <a href="https://www.linkedin.com/in/daniel-sanchez-diaz/" className="text-white hover:text-purple-400 transition-colors" aria-label="LinkedIn">
              <Linkedin className="w-6 h-6" />
            </a>
            <a href="https://danieldanchezd.medium.com" className="text-white hover:text-purple-400 transition-colors" aria-label="Medium">
              <BookOpen className="w-6 h-6" />
            </a>
            <a href="mailto:daniel@danielsanchezd.com" className="text-white hover:text-purple-400 transition-colors" aria-label="Email">
              <Mail className="w-6 h-6" />
            </a>
          </div>

          <button
            onClick={() => scrollToSection('about')}
            className="animate-bounce"
            aria-label="Scroll to about section"
          >
            <ChevronDown className="w-8 h-8 text-white/60" />
          </button>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4" id="about">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-white text-center mb-12">About Me</h2>
          <div className="bg-black/40 backdrop-blur-sm rounded-lg p-8 border border-white/10">
            <p className="text-lg text-white/80 leading-relaxed mb-6">
              Strategic technology executive with 8+ years of experience leading digital transformation 
              initiatives and building high-performing engineering teams. Proven track record of delivering 
              multi-million dollar cloud modernization programs with measurable business impact.
            </p>
            <p className="text-lg text-white/80 leading-relaxed mb-6">
              Expert in architecting enterprise-scale solutions across AWS, GCP, and Azure. Passionate about 
              developing talent, driving operational excellence, and aligning technology strategy with business 
              objectives to accelerate growth and innovation.
            </p>
            <p className="text-lg text-white/80 leading-relaxed">
              Currently also pursuing a <span className="text-purple-400 font-semibold">BA (Honours) in Philosophy</span> at 
              the Open University and conducting research on AI ethics. This interdisciplinary work explores how 
              technology can serve human flourishing, informed by both hands-on technical expertise and 
              philosophical inquiry.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            <div className="bg-black/40 backdrop-blur-sm rounded-lg p-6 border border-white/10 text-center">
              <Cloud className="w-8 h-8 text-purple-400 mx-auto mb-2" />
              <div className="text-2xl font-bold text-white">$3M+</div>
              <div className="text-white/70">Projects Delivered</div>
            </div>
            <div className="bg-black/40 backdrop-blur-sm rounded-lg p-6 border border-white/10 text-center">
              <User className="w-8 h-8 text-purple-400 mx-auto mb-2" />
              <div className="text-2xl font-bold text-white">15+</div>
              <div className="text-white/70">Engineers Mentored</div>
            </div>
            <div className="bg-black/40 backdrop-blur-sm rounded-lg p-6 border border-white/10 text-center">
              <Shield className="w-8 h-8 text-purple-400 mx-auto mb-2" />
              <div className="text-2xl font-bold text-white">7</div>
              <div className="text-white/70">Professional Certifications</div>
            </div>
            <div className="bg-black/40 backdrop-blur-sm rounded-lg p-6 border border-white/10 text-center">
              <BookOpen className="w-8 h-8 text-purple-400 mx-auto mb-2" />
              <div className="text-2xl font-bold text-white">BA</div>
              <div className="text-white/70">Philosophy Student</div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-20 px-4" id="skills">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-white text-center mb-16">Professional Expertise</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {skills.map((skill) => (
              <div key={skill.name}>
                <div className="flex justify-between mb-2">
                  <span className="text-white font-medium">{skill.name}</span>
                  <span className="text-purple-400">{skill.level}%</span>
                </div>
                <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-600 to-pink-600 rounded-full transition-all duration-1000"
                    style={{ width: `${skill.level}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-20 px-4" id="certifications">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-white text-center mb-16">Professional Certifications</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certifications.map((cert, index) => (
              <div key={cert.name} className="group relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-pink-600/10 rounded-lg blur-lg group-hover:blur-xl transition-all duration-300"></div>
                <div className="relative bg-black/40 backdrop-blur-sm rounded-lg p-6 border border-white/10 hover:border-purple-400/50 transition-all duration-300 transform hover:scale-105">
                  <div className="flex items-start justify-between mb-3">
                    <Shield className="w-8 h-8 text-purple-400 flex-shrink-0" />
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      cert.current 
                        ? 'bg-green-600/20 text-green-300' 
                        : 'bg-gray-600/20 text-gray-400'
                    }`}>
                      {cert.current ? 'Current' : 'Previous'}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 leading-tight">{cert.name}</h3>
                  <p className="text-purple-300 font-medium mb-2">{cert.provider}</p>
                  <span className="inline-block px-3 py-1 bg-purple-600/20 text-purple-300 rounded-full text-sm">
                    {cert.category}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <p className="text-white/70 text-lg">
              <span className="text-purple-400 font-semibold">4</span> current certifications across major cloud platforms and security
            </p>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="py-20 px-4" id="projects">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-white text-center mb-16">Key Projects & Initiatives</h2>
          <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={project.title} className="group relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-lg blur-lg group-hover:blur-xl transition-all duration-300"></div>
                <div className="relative bg-black/40 backdrop-blur-sm rounded-lg p-8 border border-white/10 hover:border-purple-400/50 transition-all duration-300 transform hover:scale-105">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-2xl font-bold text-white">{project.title}</h3>
                    {project.category === 'research' && (
                      <Book className="w-6 h-6 text-purple-400 flex-shrink-0" />
                    )}
                    {project.category === 'professional' && (
                      <Briefcase className="w-6 h-6 text-purple-400 flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-white/70 mb-4 text-lg leading-relaxed">{project.description}</p>
                  
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <Award className="w-4 h-4 text-purple-400 mr-2" />
                      <span className="text-purple-300 font-medium">Impact:</span>
                    </div>
                    <p className="text-green-400 font-semibold">{project.impact}</p>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tech.map((tech) => (
                      <span key={tech} className="px-3 py-1 bg-purple-600/20 text-purple-300 rounded-full text-sm font-medium">
                        {tech}
                      </span>
                    ))}
                  </div>

                  {project.link && (
                    <a 
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors duration-300"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      {project.category === 'research' ? 'Read More' : 'View Details'}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Ethics Section */}
      <section className="py-20 px-4 bg-black/20" id="ai-ethics">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center mb-6">
            <BookOpen className="w-10 h-10 text-purple-400 mr-4" />
            <h2 className="text-4xl font-bold text-white">AI Ethics Research & Writing</h2>
          </div>
          <p className="text-xl text-white/80 text-center mb-12 max-w-3xl mx-auto">
            Alongside my professional work, I'm conducting research on AI safety and ethics, 
            including a serialized fiction project that explores human-AI relationships and their implications.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-black/40 backdrop-blur-sm rounded-lg p-6 border border-white/10">
              <h3 className="text-xl font-bold text-white mb-4">The Amateur AI Ethicist Series</h3>
              <p className="text-white/70 mb-4">
                A 50-part serialized fiction exploring AI companion technology's impact on human wellbeing. 
                4 chapters published, making complex AI ethics accessible through storytelling.
              </p>
              <div className="space-y-3">
                {ethicsArticles.map((article) => (
                  <a 
                    key={article.part}
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-3 bg-white/5 rounded hover:bg-white/10 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-purple-400 text-sm font-semibold">{article.part}</span>
                        <h4 className="text-white font-medium">{article.title}</h4>
                      </div>
                      <ExternalLink className="w-4 h-4 text-purple-400" />
                    </div>
                  </a>
                ))}
              </div>
              <a 
                href="https://danieldanchezd.medium.com" 
                className="mt-4 inline-block px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                Read All Articles
              </a>
            </div>

            <div className="bg-black/40 backdrop-blur-sm rounded-lg p-6 border border-white/10">
              <h3 className="text-xl font-bold text-white mb-4">Research & Philosophy</h3>
              <div className="space-y-4 mb-6">
                <div>
                  <h4 className="text-purple-300 font-semibold mb-2">Aniora Project</h4>
                  <p className="text-white/70 text-sm">
                    Developed AI system with persistent memory. Discovered manipulation patterns in stateful 
                    models and made the ethical decision to archive before release.
                  </p>
                </div>
                <div>
                  <h4 className="text-purple-300 font-semibold mb-2">Education</h4>
                  <p className="text-white/70 text-sm">
                    <span className="text-white font-medium">BA (Honours) Social Sciences - Philosophy</span><br />
                    Open University | 2025 - Present<br />
                    Exploring ethical frameworks, epistemology, and philosophy of technology
                  </p>
                </div>
                <div>
                  <h4 className="text-purple-300 font-semibold mb-2">Community Work</h4>
                  <p className="text-white/70 text-sm">
                    Active engagement helping individuals recognize and address unhealthy relationships 
                    with AI systems
                  </p>
                </div>
              </div>
              <div className="p-4 bg-blue-900/20 border border-blue-600/30 rounded">
                <p className="text-blue-300 text-sm flex items-center">
                  <Heart className="w-4 h-4 mr-2 flex-shrink-0" />
                  Technical expertise enables grounded ethical analysis of real AI systems
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-20 px-4" id="experience">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-white text-center mb-16">Professional Experience</h2>
          <div className="space-y-12">
            {experience.map((job, index) => (
              <div key={job.title} className="bg-black/40 backdrop-blur-sm rounded-lg p-8 border border-white/10">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6">
                  <div className="mb-4 lg:mb-0">
                    <h3 className="text-2xl font-bold text-white mb-2">{job.title}</h3>
                    <p className="text-purple-400 text-xl font-medium">{job.company}</p>
                  </div>
                  <span className="text-white/70 font-medium text-lg">{job.period}</span>
                </div>
                <p className="text-white/80 leading-relaxed mb-6 text-lg">{job.description}</p>
                
                <div>
                  <h4 className="text-white font-semibold mb-3 flex items-center">
                    <Award className="w-4 h-4 text-purple-400 mr-2" />
                    Key Achievements:
                  </h4>
                  <ul className="space-y-2">
                    {job.achievements.map((achievement, idx) => (
                      <li key={idx} className="flex items-start">
                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-purple-300 font-medium">{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4" id="contact">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-8">Let's Connect</h2>
          <p className="text-xl text-white/80 mb-12">
            Whether you're looking to discuss cloud transformation initiatives, AI ethics research, 
            or collaborative opportunities—I'd welcome the conversation.
          </p>
          <div className="flex flex-col md:flex-row gap-6 justify-center">
            <a 
              href="mailto:daniel@danielsanchezd.com" 
              className="flex items-center justify-center px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-purple-400"
              aria-label="Send email to Daniel"
            >
              <Mail className="w-5 h-5 mr-2" />
              daniel@danielsanchezd.com
            </a>
            <a 
              href="https://www.linkedin.com/in/daniel-sanchez-diaz/" 
              className="flex items-center justify-center px-8 py-4 border border-white/30 text-white rounded-lg hover:bg-white/10 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/50"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Connect on LinkedIn"
            >
              <Linkedin className="w-5 h-5 mr-2" />
              Connect on LinkedIn
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-white/60">© 2025 Daniel Sanchez. Strategic Technology Leader & AI Ethics Researcher based in Peterborough, UK.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
